import { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection, getDocs, query, where, orderBy, limit,
  doc, getDoc, setDoc, deleteDoc, updateDoc, increment
} from "firebase/firestore";

export default function UserProfile({ username, currentUser, onPlaySong, currentSong, onBack }) {
  const [profile, setProfile] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [tab, setTab] = useState("songs");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [username]);

  async function loadProfile() {
    setLoading(true);
    try {
      // Search by username
      const q = query(collection(db, "users"), where("username", "==", username.replace("@", "").toLowerCase()));
      const snap = await getDocs(q);
      if (snap.empty) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      const userData = { id: snap.docs[0].id, ...snap.docs[0].data() };
      setProfile(userData);

      // Check if following
      if (currentUser) {
        const followSnap = await getDoc(doc(db, "users", currentUser.uid, "following", userData.id));
        setIsFollowing(followSnap.exists());
      }

      // Load their songs
      const songsQ = query(collection(db, "songs"), where("artistId", "==", userData.id), orderBy("createdAt", "desc"), limit(20));
      const songsSnap = await getDocs(songsQ);
      setSongs(songsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      setNotFound(true);
    }
    setLoading(false);
  }

  async function toggleFollow() {
    if (!currentUser || !profile) return;
    const followRef = doc(db, "users", currentUser.uid, "following", profile.id);
    if (isFollowing) {
      await deleteDoc(followRef);
      try { await updateDoc(doc(db, "artists", profile.id), { followers: increment(-1) }); } catch {}
      setIsFollowing(false);
      setProfile(prev => ({ ...prev, followers: (prev.followers || 1) - 1 }));
    } else {
      await setDoc(followRef, { userId: profile.id, timestamp: new Date().toISOString() });
      try { await updateDoc(doc(db, "artists", profile.id), { followers: increment(1) }); } catch {}
      setIsFollowing(true);
      setProfile(prev => ({ ...prev, followers: (prev.followers || 0) + 1 }));
    }
  }

  function shareProfile() {
    const url = `https://mymusicstream.online/@${profile.username}`;
    const text = `🎵 Check out ${profile.displayName} on MyMusic Universal Music Society!\n${url}`;
    if (navigator.share) navigator.share({ title: profile.displayName, text, url });
    else { navigator.clipboard.writeText(url); alert("Profile link copied!"); }
  }

  if (loading) return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 32, height: 32, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (notFound) return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#000", color: "#fff", fontFamily: "'DM Sans',sans-serif", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>
      <button onClick={onBack} style={{ position: "absolute", top: 20, left: 16, background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <div style={{ fontSize: 56, marginBottom: 16 }}>👤</div>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Profile Not Found</div>
      <div style={{ fontSize: 14, color: "#555" }}>@{username} doesn't exist on MyMusic</div>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "#000", color: "#fff", fontFamily: "'DM Sans',sans-serif", overflowY: "auto" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0d2818, #000)", padding: "20px 16px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <button onClick={onBack} style={{ background: "rgba(0,0,0,0.3)", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button onClick={shareProfile} style={{ background: "none", border: "1px solid #2a2a2a", color: "#fff", padding: "7px 14px", borderRadius: 50, fontSize: 12, cursor: "pointer" }}>
            📤 Share
          </button>
        </div>

        {/* Profile info */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#111", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #1db954" }}>
            {profile.photoURL ? <img src={profile.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 32 }}>👤</span>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800 }}>{profile.displayName}</div>
              {profile.memberStatus === "approved" && (
                <div style={{ background: "rgba(29,185,84,0.15)", border: "1px solid rgba(29,185,84,0.3)", borderRadius: 50, padding: "2px 8px", fontSize: 10, color: "#1db954", fontWeight: 700 }}>✓ UMS</div>
              )}
            </div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>@{profile.username}</div>
            {profile.bio && <div style={{ fontSize: 13, color: "#aaa", lineHeight: 1.6, marginBottom: 8 }}>{profile.bio}</div>}
            {profile.musicId && (
              <div style={{ fontSize: 11, color: "#333", fontFamily: "monospace" }}>{profile.musicId}</div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
          {[
            { label: "Songs", value: songs.length },
            { label: "Followers", value: profile.followers || 0 },
            { label: "Following", value: profile.following || 0 },
          ].map(stat => (
            <div key={stat.label} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800 }}>{stat.value}</div>
              <div style={{ fontSize: 11, color: "#555" }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Follow button */}
        {currentUser && currentUser.uid !== profile.id && (
          <button onClick={toggleFollow} style={{ width: "100%", padding: 12, borderRadius: 50, border: isFollowing ? "1px solid #2a2a2a" : "none", background: isFollowing ? "transparent" : "#1db954", color: isFollowing ? "#fff" : "#000", fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 16 }}>
            {isFollowing ? "✓ Following" : "+ Follow"}
          </button>
        )}

        {/* UMS ID Card */}
        {profile.memberStatus === "approved" && profile.musicId && (
          <div style={{ background: "rgba(29,185,84,0.05)", borderRadius: 12, padding: 12, border: "1px solid rgba(29,185,84,0.2)", marginBottom: 16 }}>
            <div style={{ fontSize: 9, color: "#1db954", fontWeight: 700, letterSpacing: "0.15em", marginBottom: 4 }}>🌍 UNIVERSAL MUSIC SOCIETY MEMBER</div>
            <div style={{ fontSize: 12, color: "#555", fontFamily: "monospace" }}>{profile.musicId}</div>
            <div style={{ fontSize: 11, color: "#1db954", marginTop: 4 }}>{profile.memberType || "Music Lover"}</div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid #111" }}>
          {[["songs", "🎵 Songs"], ["about", "👤 About"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ flex: 1, padding: "12px 8px", background: "none", border: "none", color: tab === id ? "#1db954" : "#555", fontSize: 13, fontWeight: tab === id ? 700 : 400, cursor: "pointer", borderBottom: tab === id ? "2px solid #1db954" : "2px solid transparent" }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Songs tab */}
      {tab === "songs" && (
        <div style={{ padding: 16 }}>
          {songs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎵</div>
              <div style={{ fontSize: 14, color: "#555" }}>No songs uploaded yet</div>
            </div>
          ) : songs.map((song, i) => {
            const playing = currentSong?.trackId === song.id;
            return (
              <div key={song.id} onClick={() => onPlaySong({ trackId: song.id, trackName: song.title, artistName: song.artistName, artworkUrl100: song.coverUrl || "", previewUrl: "", isOriginal: true, audioUrl: song.audioUrl }, songs)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer" }}>
                <span style={{ fontSize: 13, color: "#444", width: 20, textAlign: "center", flexShrink: 0 }}>{i + 1}</span>
                <div style={{ width: 46, height: 46, borderRadius: 8, background: song.coverUrl ? "transparent" : "linear-gradient(135deg,#1db954,#0a8f3c)", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: playing ? "2px solid #1db954" : "none" }}>
                  {song.coverUrl ? <img src={song.coverUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 20 }}>🎵</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: playing ? 700 : 500, color: playing ? "#1db954" : "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{song.title}</div>
                  <div style={{ fontSize: 12, color: "#555" }}>▶ {song.plays || 0} · ❤️ {song.likes || 0}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* About tab */}
      {tab === "about" && (
        <div style={{ padding: 16 }}>
          <div style={{ background: "#111", borderRadius: 14, padding: 16, border: "1px solid #1a1a1a", marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>About</div>
            <div style={{ fontSize: 14, color: "#ccc", lineHeight: 1.7 }}>{profile.bio || "No bio yet."}</div>
          </div>
          {[
            { label: "Member Since", value: profile.memberSince ? new Date(profile.memberSince).getFullYear() : "2024" },
            { label: "Member Type", value: profile.memberType || "Music Lover" },
            { label: "Profile URL", value: `mymusicstream.online/@${profile.username}` },
          ].map(item => (
            <div key={item.label} style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #0a0a0a" }}>
              <span style={{ fontSize: 13, color: "#555" }}>{item.label}</span>
              <span style={{ fontSize: 13, color: "#fff" }}>{item.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
